import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="pt-24 px-4 text-center">
        <h2 className="text-xl font-bold text-foreground-900">文章未找到</h2>
        <Link to="/" className="text-primary-600 hover:underline mt-4 inline-block">返回首页</Link>
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 4);

  return (
    <main className="w-full px-4 md:px-6 pt-24 pb-16">
      <article className="max-w-4xl mx-auto">
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Link
              to={`/category/${category?.slug || ''}`}
              className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap"
            >
              {category?.name}
            </Link>
            <span className="text-xs text-foreground-500">{article.publishDate}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-foreground-500">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.views.toLocaleString()} 阅读</span>
          </div>
        </header>

        <div className="aspect-[2/1] rounded-lg overflow-hidden mb-10">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        <div
          className="prose-custom text-foreground-800 leading-relaxed space-y-4 text-sm md:text-base"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-10 pt-8 border-t border-background-200">
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <span className="text-sm font-medium text-foreground-700">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-foreground-500">分享：</span>
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors">
              <i className="ri-wechat-line text-sm"></i>
            </span>
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors">
              <i className="ri-weibo-line text-sm"></i>
            </span>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="max-w-7xl mx-auto mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">相关推荐</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {relatedArticles.map((ra) => (
              <Link
                key={ra.id}
                to={`/article/${ra.id}`}
                className="group rounded-lg overflow-hidden bg-background-100 border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={ra.coverImage} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {ra.title}
                  </h3>
                  <span className="text-xs text-foreground-500 mt-2 inline-block">{ra.publishDate}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}